<template>
  <teleport to="body">
    <div class="notification-container">
      <TransitionGroup name="notification" tag="div" class="fixed top-4 right-4 z-50 space-y-2">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification"
          :class="[
            notification.type ? `notification-${notification.type}` : 'notification-info',
            { 'notification-dismissible': notification.dismissible !== false },
          ]"
          role="alert"
          :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
        >
          <div class="flex items-start">
            <!-- Icon -->
            <div class="flex-shrink-0">
              <component :is="getIcon(notification.type)" class="w-5 h-5" />
            </div>

            <!-- Content -->
            <div class="ml-3 flex-1">
              <div v-if="notification.title" class="text-sm font-medium">
                {{ notification.title }}
              </div>
              <div class="text-sm" :class="{ 'mt-1': notification.title }">
                {{ notification.message }}
              </div>

              <!-- Action button -->
              <div v-if="notification.action" class="mt-2">
                <button
                  class="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
                  @click="handleAction(notification)"
                >
                  {{ notification.action.label }}
                </button>
              </div>
            </div>

            <!-- Dismiss button -->
            <div v-if="notification.dismissible !== false" class="ml-4 flex-shrink-0">
              <button
                class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                @click="dismiss(notification.id)"
              >
                <span class="sr-only">Close</span>
                <svg
                  class="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Progress bar for auto-dismiss -->
          <div
            v-if="notification.duration && notification.duration > 0"
            class="absolute bottom-0 left-0 h-1 bg-current opacity-20 rounded-b"
            :style="{
              width: `${getProgressWidth(notification)}%`,
              transition: `width ${notification.duration}ms linear`,
            }"
          />
        </div>
      </TransitionGroup>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

export interface NotificationAction {
  label: string;
  handler: () => void;
}

export interface Notification {
  id: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number; // ms, 0 = no auto dismiss
  dismissible?: boolean;
  action?: NotificationAction;
  createdAt?: number;
}

// Global notifications store
const notifications = ref<Notification[]>([]);
const timers = new Map<string, number>();

// Methods
const addNotification = (notification: Omit<Notification, 'id'>) => {
  const id = generateId();
  const newNotification: Notification = {
    id,
    type: 'info',
    duration: 5000, // 5 seconds default
    dismissible: true,
    createdAt: Date.now(),
    ...notification,
  };

  notifications.value.push(newNotification);

  // Auto dismiss if duration is set
  if (newNotification.duration && newNotification.duration > 0) {
    const timer = window.setTimeout(() => {
      dismiss(id);
    }, newNotification.duration);

    timers.set(id, timer);
  }

  return id;
};

const dismiss = (id: string) => {
  const index = notifications.value.findIndex(n => n.id === id);
  if (index > -1) {
    notifications.value.splice(index, 1);
  }

  // Clear timer
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
};

const dismissAll = () => {
  notifications.value = [];
  timers.forEach(timer => clearTimeout(timer));
  timers.clear();
};

const handleAction = (notification: Notification) => {
  if (notification.action?.handler) {
    notification.action.handler();
  }
  dismiss(notification.id);
};

const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const getIcon = (type?: string) => {
  switch (type) {
    case 'success':
      return {
        template: `
          <svg class="text-success-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        `,
      };
    case 'error':
      return {
        template: `
          <svg class="text-error-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        `,
      };
    case 'warning':
      return {
        template: `
          <svg class="text-warning-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        `,
      };
    case 'info':
    default:
      return {
        template: `
          <svg class="text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        `,
      };
  }
};

const getProgressWidth = (notification: Notification): number => {
  if (!notification.duration || !notification.createdAt) return 0;

  const elapsed = Date.now() - notification.createdAt;
  const progress = Math.max(0, 100 - (elapsed / notification.duration) * 100);
  return progress;
};

// Global event listeners for adding notifications
const handleNotificationEvent = (event: CustomEvent) => {
  addNotification(event.detail);
};

// Expose methods to global scope
if (typeof window !== 'undefined') {
  window.showNotification = addNotification;
  window.dismissNotification = dismiss;
  window.dismissAllNotifications = dismissAll;
}

// Listen for global notification events
const dismissHandler = (event: Event) => {
  const customEvent = event as CustomEvent;
  dismiss(customEvent.detail?.id);
};

onMounted(() => {
  window.addEventListener('notification:show', handleNotificationEvent as EventListener);
  window.addEventListener('notification:dismiss', dismissHandler);
  window.addEventListener('notification:dismiss-all', dismissAll);
});

onUnmounted(() => {
  window.removeEventListener('notification:show', handleNotificationEvent as EventListener);
  window.removeEventListener('notification:dismiss', dismissHandler);
  window.removeEventListener('notification:dismiss-all', dismissAll);

  // Clear all timers
  timers.forEach(timer => clearTimeout(timer));
  timers.clear();
});

// Utility functions for different notification types
const showSuccess = (message: string, options?: Partial<Notification>) => {
  return addNotification({ ...options, type: 'success', message });
};

const showError = (message: string, options?: Partial<Notification>) => {
  return addNotification({ ...options, type: 'error', message, duration: 0 }); // Don't auto-dismiss errors
};

const showWarning = (message: string, options?: Partial<Notification>) => {
  return addNotification({ ...options, type: 'warning', message });
};

const showInfo = (message: string, options?: Partial<Notification>) => {
  return addNotification({ ...options, type: 'info', message });
};

// Export methods for use in components
defineExpose({
  addNotification,
  dismiss,
  dismissAll,
  showSuccess,
  showError,
  showWarning,
  showInfo,
});

// Make methods available globally
if (typeof window !== 'undefined') {
  (window as any).notifications = {
    show: addNotification,
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    dismiss,
    dismissAll,
  };
}
</script>

<style scoped>
.notification-container {
  pointer-events: none;
}

.notification {
  @apply relative max-w-sm w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto;
  @apply ring-1 ring-black ring-opacity-5 overflow-hidden;
  @apply p-4;
}

.notification-success {
  @apply border-l-4 border-success-400 bg-success-50 dark:bg-success-900/20;
}

.notification-success .text-sm {
  @apply text-success-800 dark:text-success-200;
}

.notification-success .font-medium {
  @apply text-success-900 dark:text-success-100;
}

.notification-error {
  @apply border-l-4 border-error-400 bg-error-50 dark:bg-error-900/20;
}

.notification-error .text-sm {
  @apply text-error-800 dark:text-error-200;
}

.notification-error .font-medium {
  @apply text-error-900 dark:text-error-100;
}

.notification-warning {
  @apply border-l-4 border-warning-400 bg-warning-50 dark:bg-warning-900/20;
}

.notification-warning .text-sm {
  @apply text-warning-800 dark:text-warning-200;
}

.notification-warning .font-medium {
  @apply text-warning-900 dark:text-warning-100;
}

.notification-info {
  @apply border-l-4 border-blue-400 bg-blue-50 dark:bg-blue-900/20;
}

.notification-info .text-sm {
  @apply text-blue-800 dark:text-blue-200;
}

.notification-info .font-medium {
  @apply text-blue-900 dark:text-blue-100;
}

/* Transition animations */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .notification-enter-active,
  .notification-leave-active,
  .notification-move {
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .notification {
    @apply border-2;
  }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .notification-container .fixed {
    @apply left-4 right-4 top-4;
  }

  .notification {
    @apply max-w-none;
  }
}
</style>
